import { v4 } from 'uuid';

import { appEnv as getAppEnv, oidcClientId } from '../constants/env';
import { type MetaTag, metaTagList } from '../constants/metaTag';
import { appUrl, portalUrl } from '../constants/url';
import {
  createAuthorizationRequest,
  isAuthorizationRequest,
  isAuthorizationRequestQueryParams,
} from '../domain/auth';
import { isSuccessResult } from '../domain/result';
import { confirmAuthenticatedInGetServerSideProps } from '../services/confirmAuthenticated';
import {
  isRegistrationOptionalParams,
  saveRedirectUri,
} from '../services/registrationOptionalParams';
import { RegistrationTemplate } from '../templates';

import type { AppEnv, HttpsUrl } from '@/packages/nurse-senka-frontend-sdk';
import type { AuthorizationRequest } from '@/packages/nurse-senka-web-ui';
import type { GetServerSideProps, NextPage } from 'next';

type Props = {
  appEnv: AppEnv;
  metaTag: MetaTag;
  redirectUri?: HttpsUrl;
  authorizationRequest?: AuthorizationRequest;
};

const RegistrationsPage: NextPage<Props> = ({
  appEnv,
  metaTag,
  redirectUri,
  authorizationRequest,
}) => (
  <RegistrationTemplate
    appEnv={appEnv}
    metaTag={metaTag}
    redirectUri={redirectUri}
    initAuthorizationRequest={authorizationRequest}
  />
);

// eslint-disable-next-line require-await
export const getServerSideProps: GetServerSideProps = async (context) => {
  const props: Props = {
    appEnv: getAppEnv(),
    metaTag: metaTagList().registrations,
  };

  if (isRegistrationOptionalParams(context.query)) {
    props.redirectUri = context.query.redirect_uri;
    saveRedirectUri(
      { res: context.res },
      context.query.redirect_uri,
      props.appEnv,
    );
  } else {
    props.redirectUri = portalUrl();
    saveRedirectUri({ res: context.res }, props.redirectUri, props.appEnv);
  }

  if (isAuthorizationRequestQueryParams(context.query)) {
    const authorizationRequest = {
      clientId: Number(context.query.client_id),
      redirectUri: context.query.redirect_uri,
      state: context.query.state,
      nonce: context.query.nonce,
    };

    if (isAuthorizationRequest(authorizationRequest)) {
      props.authorizationRequest = authorizationRequest;
    }
  } else {
    props.authorizationRequest = createAuthorizationRequest({
      clientId: oidcClientId(),
      redirectUri: appUrl.top,
      generateUniqueId: v4,
    });
  }

  const confirmAuthenticatedResult =
    await confirmAuthenticatedInGetServerSideProps(
      { req: context.req, res: context.res },
      props.appEnv,
      appUrl.top,
    );

  if (isSuccessResult(confirmAuthenticatedResult)) {
    return {
      redirect: {
        permanent: false,
        destination: props.authorizationRequest?.redirectUri
          ? props.authorizationRequest?.redirectUri
          : appUrl.top,
      },
    };
  }

  return {
    props,
  };
};

export default RegistrationsPage;
